import {
  AuthenticatorBuilder,
  DeviceRole,
  InstantLinkResultType,
  MobileAuthImplementation,
  OtpFinishResultType,
  type OtpError,
  type OtpFinishResult,
  type OtpStartInput,
  type PhoneValidationError,
} from '@prove-identity/prove-auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProveAuthAction, ProveAuthFallbackReason, ProveAuthMethod, ProveFlowType } from './proveAnalyticsEvents';

/**
 * The Prove SDK orchestration (AuthenticatorBuilder wiring, instant-link / OTP fallbacks, resend
 * throttling) lifted out of ProveMobileAuth so the same logic drives both the legacy /login/mobile
 * screen and the in-wizard PROVE_STEP (XF-10207) without a second copy. The hook owns only SDK
 * state; what happens on finish (prove/complete) and on fallback is the caller's.
 *
 * Everything here lives in component memory. PROVE_STEP's run-once guarantee depends on a hard
 * reload discarding the in-flight session rather than resuming it, which only holds if the Prove
 * state never leaves this hook — do not persist any of it.
 */

export type InstantLinkStatus = 'idle' | 'sending' | 'sent';

export type ProveFallbackMeta = {
  source: string;
  fallbackReason?: ProveAuthFallbackReason;
  httpStatus?: number;
};

export type ProveAuthenticatorBuilderFactory = () => AuthenticatorBuilder;

type OtpResolver = {
  reject: (error?: unknown) => void;
  resolve: (value: OtpFinishResult) => void;
};

type InstantLinkResolver = {
  reject: (error?: unknown) => void;
  resolve: (value: InstantLinkResultType) => void;
};

// What /client/prove/complete answers when Prove could not verify the applicant. Not 401: the unified
// client treats a 401 on a /client route as an expired session and refreshes and replays the request.
export const PROVE_REJECTED_STATUS = 422;
export const RESEND_THROTTLE_MS = 60_000;
const SENT_STATUS_DISPLAY_MS = 5_000;
export const INSTANT_LINK_RESEND_THROTTLE_ERROR_ID = 'proveMobileAuth.instantLink.resendThrottleError';

declare global {
  interface Window {
    /** Dev/test-only seam (Storybook, Playwright): replaces the real Prove SDK builder. Never read in production builds. */
    __odProveAuthenticatorBuilder?: ProveAuthenticatorBuilderFactory;
  }
}

export const defaultProveAuthenticatorBuilderFactory: ProveAuthenticatorBuilderFactory = () => {
  if (import.meta.env?.MODE !== 'production' && typeof window !== 'undefined' && typeof window.__odProveAuthenticatorBuilder === 'function') {
    return window.__odProveAuthenticatorBuilder();
  }

  return new AuthenticatorBuilder();
};

export interface UseProveAuthenticatorOptions {
  /** Prove authToken from prove/start. Missing → immediate fallback (session expired). */
  authToken?: string;
  flowType: ProveFlowType;
  /** Digits-only or formatted phone; needed when the SDK asks for an OTP target. */
  phoneNumber?: string;
  /** The SDK's auth-finish step: exchange the session (prove/complete). Throw to fall back. */
  onFinish: () => Promise<void>;
  /** Prove cannot complete here (SDK error, missing token/phone, finish threw). Called at most once per failure source. */
  onFallback: (error: string | undefined, meta: ProveFallbackMeta) => void;
  /** SDK milestones for the funnel (ChallengeSent, WrongCodeSubmitted, ResendRequested). */
  track?: (action: ProveAuthAction, properties?: Record<string, unknown>, method?: ProveAuthMethod) => void;
  /** Test seam; defaults to the real SDK (or the dev-only window stub). */
  createAuthenticatorBuilder?: ProveAuthenticatorBuilderFactory;
}

export interface ProveAuthenticatorControls {
  instantLinkStatus: InstantLinkStatus;
  otpError?: string;
  /** Message id to render when a resend was throttled. */
  resendThrottleErrorId?: string;
  sdkError?: string;
  /** OTP resend confirmation shown briefly. */
  sent: boolean;
  resendInstantLink: () => void;
  resendOtp: () => void;
  submitOtp: (otp: string) => void;
  /** Clear SDK/OTP/throttle error state (e.g. before showing a caller-owned error). */
  clearErrors: () => void;
  /** Clear errors AND the in-flight instant-link state; what the fallback screen needs. */
  reset: () => void;
}

export function useProveAuthenticator({
  authToken,
  flowType,
  phoneNumber,
  onFinish,
  onFallback,
  track,
  createAuthenticatorBuilder = defaultProveAuthenticatorBuilderFactory,
}: UseProveAuthenticatorOptions): ProveAuthenticatorControls {
  const startedRef = useRef(false);
  const otpResolverRef = useRef<OtpResolver | undefined>(undefined);
  // A code submitted before the SDK has armed its OTP step (it does so only after its own start
  // step resolves) used to be dropped silently. Hold it and hand it over the moment the step arms.
  const pendingOtpRef = useRef<string | undefined>(undefined);
  const instantLinkResolverRef = useRef<InstantLinkResolver | undefined>(undefined);
  const otpInitialSentRef = useRef(false);
  const instantLinkInitialSentRef = useRef(false);
  const instantLinkQueuedResendRef = useRef(false);
  const instantLinkResendRequestedRef = useRef(false);
  const lastInstantLinkSentAtRef = useRef<number | undefined>(undefined);
  const instantLinkSentTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const resendThrottleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Callers pass fresh closures every render; the SDK effect must see the latest without re-running.
  const callbacksRef = useRef({ createAuthenticatorBuilder, onFallback, onFinish, track });
  callbacksRef.current = { createAuthenticatorBuilder, onFallback, onFinish, track };

  const [sent, setSent] = useState(false);
  const [instantLinkSent, setInstantLinkSent] = useState(false);
  const [instantLinkSending, setInstantLinkSending] = useState(false);
  const [sdkError, setSdkError] = useState<string>();
  const [resendThrottleErrorId, setResendThrottleErrorId] = useState<string>();
  const [otpError, setOtpError] = useState<string>();

  const trackAuth = useCallback((action: ProveAuthAction, properties: Record<string, unknown> = {}, method?: ProveAuthMethod) => {
    callbacksRef.current.track?.(action, properties, method);
  }, []);
  const fallback = useCallback((error: string | undefined, meta: ProveFallbackMeta) => {
    callbacksRef.current.onFallback(error, meta);
  }, []);

  const clearResendThrottleError = () => {
    if (resendThrottleTimeoutRef.current) clearTimeout(resendThrottleTimeoutRef.current);
    resendThrottleTimeoutRef.current = undefined;
    setResendThrottleErrorId(undefined);
  };
  const showResendThrottleError = (lastSentAt?: number) => {
    if (resendThrottleTimeoutRef.current) clearTimeout(resendThrottleTimeoutRef.current);
    const remainingMs = getResendThrottleRemainingMs(lastSentAt);
    if (remainingMs <= 0) {
      resendThrottleTimeoutRef.current = undefined;
      setResendThrottleErrorId(undefined);
      return;
    }

    setResendThrottleErrorId(INSTANT_LINK_RESEND_THROTTLE_ERROR_ID);
    resendThrottleTimeoutRef.current = setTimeout(() => {
      setResendThrottleErrorId(undefined);
      resendThrottleTimeoutRef.current = undefined;
    }, remainingMs);
  };
  const clearErrors = () => {
    setSdkError(undefined);
    setOtpError(undefined);
    clearResendThrottleError();
  };
  const reset = () => {
    clearErrors();
    setInstantLinkSending(false);
    setInstantLinkSent(false);
    instantLinkQueuedResendRef.current = false;
    instantLinkResendRequestedRef.current = false;
    lastInstantLinkSentAtRef.current = undefined;
    if (instantLinkSentTimeoutRef.current) clearTimeout(instantLinkSentTimeoutRef.current);
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!authToken) {
      fallback('Your verification session expired. Please restart the Prove flow.', { source: 'MissingAuthToken' });
      return;
    }

    let cancelled = false;

    (async () => {
      if (cancelled) return;
      const startPossessionCheck = (phoneNumberNeeded: boolean, phoneValidationError?: PhoneValidationError) => new Promise<OtpStartInput | null>((resolve, reject) => {
        if (phoneValidationError?.message) {
          setInstantLinkSending(false);
          fallback(phoneValidationError.message, { source: 'PhoneValidationError' });
          reject(phoneValidationError.message);
          return;
        }

        if (phoneNumberNeeded) {
          const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
          if (!normalizedPhoneNumber) {
            setInstantLinkSending(false);
            fallback('Phone number was not available for Prove OTP.', { source: 'MissingPhoneNumber' });
            return reject('Phone number was not available for Prove OTP.');
          }

          resolve({ phoneNumber: normalizedPhoneNumber });
          return;
        }

        resolve(null);
      });
      const instantLinkRetry = () => new Promise<InstantLinkResultType>((resolve, reject) => {
        if (instantLinkQueuedResendRef.current) {
          instantLinkQueuedResendRef.current = false;
          instantLinkResendRequestedRef.current = true;
          resolve(InstantLinkResultType.OnResend);
          return;
        }

        setInstantLinkSending(false);
        if (instantLinkResendRequestedRef.current) {
          instantLinkResendRequestedRef.current = false;
          lastInstantLinkSentAtRef.current = Date.now();
          setInstantLinkSent(true);
          clearResendThrottleError();
          if (instantLinkSentTimeoutRef.current) clearTimeout(instantLinkSentTimeoutRef.current);
          instantLinkSentTimeoutRef.current = setTimeout(() => setInstantLinkSent(false), SENT_STATUS_DISPLAY_MS);
        } else {
          if (!instantLinkInitialSentRef.current) {
            instantLinkInitialSentRef.current = true;
            lastInstantLinkSentAtRef.current = Date.now();
            trackAuth('ChallengeSent');
          }
          setInstantLinkSent(false);
        }
        instantLinkResolverRef.current = { resolve, reject };
      });
      const otpFinish = (proseOtpError?: OtpError) => new Promise<OtpFinishResult>((resolve, reject) => {
        if (!otpInitialSentRef.current) {
          otpInitialSentRef.current = true;
          trackAuth('ChallengeSent');
        }
        setSdkError(undefined);
        setOtpError(normalizeOtpErrorMessage(proseOtpError?.message));
        if (proseOtpError) trackAuth('WrongCodeSubmitted');

        const pendingOtp = pendingOtpRef.current;
        pendingOtpRef.current = undefined;
        // Never auto-resubmit a code the SDK just rejected.
        if (pendingOtp && !proseOtpError) {
          resolve({ input: { otp: pendingOtp }, resultType: OtpFinishResultType.OnSuccess });
          return;
        }

        otpResolverRef.current = { resolve, reject };
      });
      let builder = callbacksRef.current.createAuthenticatorBuilder()
        .withAuthFinishStep(async () => {
          await callbacksRef.current.onFinish();
        });

      builder = flowType === 'mobile'
        ? builder
          .withMobileAuthImplementation(MobileAuthImplementation.Fetch)
          .withOtpFallback(startPossessionCheck, otpFinish)
        : builder
          .withRole(DeviceRole.Secondary)
          .withInstantLinkFallback(startPossessionCheck, instantLinkRetry);

      await builder.build().authenticate(authToken);
    })().catch((error) => {
      if (!cancelled) {
        fallback(getProveFallbackError(error), {
          source: 'ProveAuthenticateError',
          httpStatus: getErrorStatus(error),
        });
      }
    });

    return () => {
      cancelled = true;
      otpResolverRef.current?.reject('Prove authentication was cancelled.');
      otpResolverRef.current = undefined;
      pendingOtpRef.current = undefined;
      instantLinkResolverRef.current?.reject('Prove authentication was cancelled.');
      instantLinkResolverRef.current = undefined;
      otpInitialSentRef.current = false;
      instantLinkInitialSentRef.current = false;
      instantLinkQueuedResendRef.current = false;
      instantLinkResendRequestedRef.current = false;
      lastInstantLinkSentAtRef.current = undefined;
      if (instantLinkSentTimeoutRef.current) clearTimeout(instantLinkSentTimeoutRef.current);
      if (resendThrottleTimeoutRef.current) clearTimeout(resendThrottleTimeoutRef.current);
    };
  }, [authToken, flowType, phoneNumber, fallback, trackAuth]);

  const resendInstantLink = () => {
    const resolver = instantLinkResolverRef.current;
    if (instantLinkSending) return;

    trackAuth('ResendRequested');

    if (wasSentRecently(lastInstantLinkSentAtRef.current)) {
      if (instantLinkSentTimeoutRef.current) clearTimeout(instantLinkSentTimeoutRef.current);
      setInstantLinkSent(false);
      setSdkError(undefined);
      showResendThrottleError(lastInstantLinkSentAtRef.current);
      setOtpError(undefined);
      return;
    }

    if (instantLinkSentTimeoutRef.current) clearTimeout(instantLinkSentTimeoutRef.current);
    setInstantLinkSent(false);
    setInstantLinkSending(true);
    setSdkError(undefined);
    clearResendThrottleError();
    setOtpError(undefined);

    if (!resolver) {
      instantLinkQueuedResendRef.current = true;
      return;
    }

    instantLinkResendRequestedRef.current = true;
    resolver.resolve(InstantLinkResultType.OnResend);
    instantLinkResolverRef.current = undefined;
  };

  const submitOtp = (otp: string) => {
    const resolver = otpResolverRef.current;
    setSdkError(undefined);
    setOtpError(undefined);

    if (!resolver) {
      pendingOtpRef.current = otp;
      return;
    }

    resolver.resolve({
      input: { otp },
      resultType: OtpFinishResultType.OnSuccess,
    });
    otpResolverRef.current = undefined;
  };

  const resendOtp = () => {
    const resolver = otpResolverRef.current;
    if (!resolver) return;

    trackAuth('ResendRequested');

    resolver.resolve({
      resultType: OtpFinishResultType.OnResendOtp,
    });
    otpResolverRef.current = undefined;
    setSent(true);
    setSdkError(undefined);
    setOtpError(undefined);
    setTimeout(() => setSent(false), SENT_STATUS_DISPLAY_MS);
  };

  return {
    instantLinkStatus: instantLinkSending ? 'sending' : instantLinkSent ? 'sent' : 'idle',
    otpError,
    resendThrottleErrorId,
    sdkError,
    sent,
    resendInstantLink,
    resendOtp,
    submitOtp,
    clearErrors,
    reset,
  };
}

function normalizeOtpErrorMessage(message?: string): string | undefined {
  if (!message) return undefined;
  return 'Incorrect code. Please try again.';
}

function wasSentRecently(lastSentAt?: number) {
  return getResendThrottleRemainingMs(lastSentAt) > 0;
}

function getResendThrottleRemainingMs(lastSentAt?: number) {
  if (lastSentAt === undefined) return 0;
  return Math.max(0, RESEND_THROTTLE_MS - (Date.now() - lastSentAt));
}

function normalizePhoneNumber(value?: string) {
  const digits = value?.replace(/\D/g, '');

  return digits || undefined;
}

export function getProveFallbackError(error: unknown) {
  return getErrorMessage(getRecordValue(error, 'data'))
    ?? getErrorMessage(getRecordValue(error, 'data$'))
    ?? getErrorMessage(error)
    ?? 'Unable to start Prove authentication.';
}

export function getErrorStatus(value: unknown): number | undefined {
  if (typeof value !== 'object' || value === null) return undefined;

  const status = getRecordValue(value, 'status');
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    const parsed = Number(status);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return getErrorStatus(getRecordValue(value, 'data'))
    ?? getErrorStatus(getRecordValue(value, 'data$'));
}

function getErrorMessage(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (value instanceof Error) return value.message.trim() || undefined;
  if (typeof value !== 'object' || value === null) return undefined;

  const message = getRecordValue(value, 'message');
  if (typeof message === 'string') return message.trim() || undefined;

  return getErrorMessage(getRecordValue(value, 'data'))
    ?? getErrorMessage(getRecordValue(value, 'data$'));
}

function getRecordValue(value: unknown, key: string) {
  return typeof value === 'object' && value !== null && key in value
    ? (value as Record<string, unknown>)[key]
    : undefined;
}
